import { groupBy, max } from 'lodash';

import {
  INTER_SECTION_NODE_PADDING,
  SECTION_LABEL_NODE_PADDING,
} from '@/components/diagrams/FlowChart/constants';
import { Edge, Node } from '@/components/diagrams/FlowChart/types';
import { gridLayout } from '@/components/diagrams/FlowChart/utils/gridLayout';
import {
  getBoundingBoxForNodes,
  isSectionLabelChildNode,
  isSectionLabelNode,
} from '@/components/diagrams/FlowChart/utils/nodes';

import {
  ENTITY_DIAGRAM_GRID_CHUNK_SIZE,
  SINGLE_ENTITY_DIAGRAM_TARGET_ASPECT_RATIO,
  X_SPACING,
  Y_SPACING,
} from '../EntityDiagram.constants';
import { EntityDiagramVariant } from '../types';

/**
 * Lower bound for the stretch target aspect (width / height). Prevents
 * an unusually tall container from pushing the diagram to a visually
 * absurd tall-and-narrow layout with huge gaps between sections. At
 * 0.7, the stretched diagram is never more than ~1.43× as tall as wide.
 */
const MIN_TARGET_ASPECT_RATIO = 0.7;

interface Opts {
  nodes: Node[];
  edges?: Edge[];
  isInHalfWidthContainer?: boolean;
  entityDiagramVariant?: EntityDiagramVariant;
  /**
   * Override for the stretch target aspect ratio. When provided,
   * overrides the fixed {@link SINGLE_ENTITY_DIAGRAM_TARGET_ASPECT_RATIO}
   * constant — used for the DP V2 dispositions form where the container
   * shape varies freely with window size. Clamped by
   * {@link MIN_TARGET_ASPECT_RATIO} to avoid pathological stretch on
   * very tall containers. Presentation slides omit this and fall back
   * to the constant.
   */
  targetAspectRatio?: number;
}

/**
 * Result of {@link getDispositionsInterSectionPadding}. Two paired values
 * that together describe how the diagram's vertical space should be
 * distributed across sections:
 *
 *  - `interSectionPadding` — gap between a section's tiles-bottom and the
 *    next section's label. Scalar for uniform gaps, array indexed by gap
 *    (gap `i` = between sections `i` and `i+1` in document order).
 *  - `labelPadding` — gap between a section's label and its first tile.
 *    Scalar for uniform padding, array indexed by section document order.
 *    The first section's entry stays at the base padding (no gap above it
 *    to split).
 *
 * The stretch extra is distributed so every middle section ends up
 * **block-centered** within its slot: the extra padding added above
 * the section (inter-section gap) equals the extra added below it. A
 * single common `y` is applied to every inter-section gap — defaulting
 * to `extra[0] / 2` so the first gap is split 50/50 around section 1's
 * label — and each gap's remaining stretch flows into the label→tiles
 * padding of the section below it. Total vertical extent is preserved.
 */
export interface DispositionsSectionPadding {
  interSectionPadding: number | number[];
  labelPadding: number | number[];
}

const BASE_PADDING: DispositionsSectionPadding = {
  interSectionPadding: INTER_SECTION_NODE_PADDING,
  labelPadding: SECTION_LABEL_NODE_PADDING,
};

/**
 * When an entity diagram renders inside a tall half-width container
 * (e.g. the DP V2 form split-screen, or a presentation diagram slide
 * alongside notes), compute enlarged inter-section gaps so the diagram
 * stretches vertically toward
 * {@link SINGLE_ENTITY_DIAGRAM_TARGET_ASPECT_RATIO} and fills more of
 * its container. Returns the base constants in all other cases.
 *
 * When there are 2+ gaps (three-section / two-client households), the
 * extra stretch is distributed **asymmetrically** across gaps: each gap
 * is weighted by the tile count of the sections on either side of it.
 * A sparse section (e.g. "Before first death" when it only contains the
 * grantor tile) therefore gets a smaller neighboring gap, which pulls
 * the sparse section closer to the rest of the diagram and reduces the
 * appearance of empty space. Total stretched height is unchanged — only
 * the distribution across gaps shifts.
 *
 * The per-gap extra is then split **50/50** between the gap itself
 * (above the next section's label) and the next section's label
 * padding (below the label, above its first tile). Centering the label
 * in its expanded slot avoids the "long empty line above a floating
 * label" look where all the extra lived between prev-tiles-bottom and
 * next-section-label.
 *
 * The calculation is derived purely from *measured tile dimensions* and
 * the section structure — never from current tile positions. This keeps
 * the function idempotent: calling it repeatedly (e.g. after
 * `applySectionBoundsToState` stretches sections apart, then React Flow
 * fires a measurement event that re-runs the calc) always returns the
 * same padding for a given set of measured tiles, so the stretch does
 * not oscillate.
 *
 * Only stretches Dispositions-variant diagrams — other variants
 * (Ownership, GratTerm, etc.) don't lay out recipient rows the same
 * way and are left at the base inter-section padding.
 */
export function getDispositionsInterSectionPadding({
  nodes,
  edges = [],
  isInHalfWidthContainer,
  entityDiagramVariant,
  targetAspectRatio,
}: Opts): DispositionsSectionPadding {
  if (
    !isInHalfWidthContainer ||
    entityDiagramVariant !== EntityDiagramVariant.Dispositions
  ) {
    return BASE_PADDING;
  }

  const effectiveTarget = Math.max(
    targetAspectRatio ?? SINGLE_ENTITY_DIAGRAM_TARGET_ASPECT_RATIO,
    MIN_TARGET_ASPECT_RATIO
  );

  const tileNodes = nodes.filter(isSectionLabelChildNode);
  if (tileNodes.length === 0) return BASE_PADDING;

  // Order sections by their section-label document order. Matters
  // because the section-bounds handler iterates sections via
  // `nextNodes.filter(isSectionLabelNode)` and applies our per-gap
  // padding array by that same index — so the array we return MUST be
  // ordered to match. `groupBy(tileNodes, sectionLabelId)` alone would
  // order by first-tile-encounter, which diverges from document order
  // when placeholder tiles for empty sections are appended to the end
  // of the node list (see getDispositionsVizFromEntity.tsx).
  const sectionIdOrder = nodes.filter(isSectionLabelNode).map((n) => n.id);
  const sections = groupBy(tileNodes, (n) => n.data.sectionLabelId);
  const sectionGroups = sectionIdOrder.map((id) => sections[id] ?? []);
  if (sectionGroups.length < 2) return BASE_PADDING;

  // Measure an unstretched layout rather than counting tiles as columns.
  // Connected stacks are one column and contribute their full height.
  const sectionBounds = sectionGroups.map((sectionNodes) => {
    if (sectionNodes.length === 0) return { width: 0, height: 0 };
    const { grid } = gridLayout({
      // Layout mutates positions. Do not change the live diagram or its history.
      nodes: sectionNodes.map((node) => ({
        ...node,
        position: { ...node.position },
      })),
      edges,
      chunkSize: ENTITY_DIAGRAM_GRID_CHUNK_SIZE,
      paddingX: X_SPACING,
      paddingY: Y_SPACING,
    });
    const bounds = getBoundingBoxForNodes(grid.flat());
    return {
      width: bounds.right - bounds.left,
      height: bounds.bottom - bounds.top,
    };
  });
  const naturalWidth = max(sectionBounds.map((bounds) => bounds.width)) ?? 0;

  // Natural (unstretched) height: per-section tile heights summed with
  // base inter-section gaps plus the per-section label padding that the
  // section-bounds handler adds above each section's first tile. Matches
  // the actual bounding box the diagram renders into, so the computed
  // aspect ratio doesn't under-estimate height and over-shoot the stretch.
  const sectionHeights = sectionBounds.map((bounds) => bounds.height);
  const baseInterSectionTotal =
    INTER_SECTION_NODE_PADDING * (sectionGroups.length - 1);
  const labelPaddingTotal = SECTION_LABEL_NODE_PADDING * sectionGroups.length;
  const naturalHeight =
    sectionHeights.reduce((a, b) => a + b, 0) +
    baseInterSectionTotal +
    labelPaddingTotal;

  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return BASE_PADDING;
  }

  const naturalAspectRatio = naturalWidth / naturalHeight;
  if (naturalAspectRatio <= effectiveTarget) {
    return BASE_PADDING;
  }

  const targetHeight = naturalWidth / effectiveTarget;
  const gapCount = sectionGroups.length - 1;
  const totalExtra = targetHeight - naturalHeight;

  // Per-gap extra (before the 50/50 split). For a single gap the full
  // totalExtra goes there; for multiple gaps the extra is weighted by
  // adjacent section tile counts so sparse sections get smaller gaps.
  let perGapExtra: number[];
  if (gapCount === 1) {
    perGapExtra = [totalExtra];
  } else {
    // Weight each gap by the *tile count* of the sections on either
    // side, capped at one row's worth
    // ({@link ENTITY_DIAGRAM_GRID_CHUNK_SIZE}). Sections with fewer
    // tiles contribute less visual weight, so adjacent gaps shrink.
    // The cap avoids double-counting tiles past the first row: tiles
    // that wrap add to the section's measured height (already folded
    // into `naturalHeight`), so letting them add weight again would
    // over-amplify the bottom section's share of the stretch. Using
    // counts instead of measured heights catches cases where multiple
    // stacked sections each hold a single tile (so their heights are
    // identical and a height-based weight would still come out
    // symmetric) — the bottom section that holds most of the tiles
    // still dominates, which is what gives the diagram a natural
    // top-to-bottom "expanding tree" feel. Falls back to even
    // distribution if total weight would be zero.
    const gapWeights = Array.from({ length: gapCount }, (_, i) => {
      const prev = Math.min(
        sectionGroups[i]?.length ?? 0,
        ENTITY_DIAGRAM_GRID_CHUNK_SIZE
      );
      const next = Math.min(
        sectionGroups[i + 1]?.length ?? 0,
        ENTITY_DIAGRAM_GRID_CHUNK_SIZE
      );
      return prev + next;
    });
    const totalWeight = gapWeights.reduce((a, b) => a + b, 0);
    if (totalWeight <= 0) {
      perGapExtra = Array.from(
        { length: gapCount },
        () => totalExtra / gapCount
      );
    } else {
      perGapExtra = gapWeights.map((w) => (totalExtra * w) / totalWeight);
    }
  }

  // Block-center each middle section: pick a single above-label stretch
  // value `y` that applies uniformly to every inter-section gap. Because
  // each middle section's "above-block delta" and "below-block delta"
  // both equal `y`, the section block (label + labelPadding + tiles)
  // ends up symmetrically padded above and below.
  //
  // `y` defaults to the first gap's 50/50 split (`extra[0] / 2`), which
  // matches the natural "split gap 0 evenly around section 1's label"
  // intuition. We cap at `min(extra)` so no gap's remaining (below-label)
  // stretch goes negative — an edge case when a later gap has less
  // slack than `extra[0] / 2`.
  //
  // Each gap's remaining stretch (`extra[i] - y`) flows into the
  // label→tiles padding of the section *below* the gap. Section 0 has
  // no gap above it, so its labelPadding stays at the base constant.
  const halfFirstGap = perGapExtra[0]! / 2;
  const minExtra = Math.min(...perGapExtra);
  const y = Math.min(halfFirstGap, minExtra);
  const interSectionPaddingArr = perGapExtra.map(
    () => INTER_SECTION_NODE_PADDING + y
  );
  const labelPaddingArr = [
    SECTION_LABEL_NODE_PADDING,
    ...perGapExtra.map((extra) => SECTION_LABEL_NODE_PADDING + (extra - y)),
  ];

  return {
    // Preserve the scalar return shape for single-gap layouts so
    // downstream consumers can special-case them cheaply.
    interSectionPadding:
      interSectionPaddingArr.length === 1
        ? interSectionPaddingArr[0]!
        : interSectionPaddingArr,
    labelPadding: labelPaddingArr,
  };
}

/**
 * Value-equality for one component of {@link DispositionsSectionPadding}.
 * Used by {@link sectionPaddingEqual} to stabilize the reference across
 * renders so downstream `useCallback` deps don't bust every time a fresh
 * array (with identical contents) is produced.
 */
export function interSectionPaddingEqual(
  a: number | number[],
  b: number | number[]
): boolean {
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
  }
  return false;
}

/**
 * Value-equality for the full {@link DispositionsSectionPadding} result.
 */
export function sectionPaddingEqual(
  a: DispositionsSectionPadding,
  b: DispositionsSectionPadding
): boolean {
  return (
    interSectionPaddingEqual(a.interSectionPadding, b.interSectionPadding) &&
    interSectionPaddingEqual(a.labelPadding, b.labelPadding)
  );
}
